import React, { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react";

// Modes the rep can pick for a research run. Each entry carries the
// time-to-complete estimate and credit cost so the dropdown can show
// the rep what they're trading off (faster = pricier, slower = cheaper).
// Credit values mirror TeamServiceImpl.DR_INSTANT_CREDITS (40) and
// DR_CREDITS (20) on the backend — keep these in lockstep.
const MODES = [
  { value: "instant", label: "Instant", icon: "⚡", time: "~30s", credits: 40 },
  { value: "smart-saver", label: "Smart Saver", icon: "⏳", time: "~60s", credits: 20 }
];

const labelFor = (p) =>
  p?.name || p?.company_name || p?.product_name || "Unnamed profile";

const SellerProfileBar = observer(({
  profiles = [],
  activeProfileId,
  loadedResearchProfileId,
  mode,
  onModeChange,
  onSelectProfile,
  onAddNewProfile,
  onEditProfile
}) => {
  const [open, setOpen] = useState(false);
  const [modeOpen, setModeOpen] = useState(false);
  const ref = useRef(null);
  const modeRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!modeOpen) return;
    const onDoc = (e) => {
      if (modeRef.current && !modeRef.current.contains(e.target)) setModeOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [modeOpen]);

  const currentMode = MODES.find((m) => m.value === mode) || MODES[0];

  const hasProfiles = profiles.length > 0;
  const activeProfile = profiles.find(p => p.id === activeProfileId) || null;
  const triggerLabel = activeProfile ? labelFor(activeProfile) : "No seller profile";

  const handlePick = (id) => {
    onSelectProfile && onSelectProfile(id);
    setOpen(false);
  };

  const handleAddNew = () => {
    setOpen(false);
    onAddNewProfile && onAddNewProfile();
  };

  return (
    <div className="drSellerBar">
      <div className="drSellerInfo">
        <span className="drSellerLabel">Seller:</span>

        {hasProfiles ? (
          <div className="drSellerDropdown" ref={ref}>
            <button
              type="button"
              className={`drSellerDropdownTrigger ${open ? "active" : ""}`}
              onClick={() => setOpen(o => !o)}
            >
              <span className="drSellerDropdownText">{triggerLabel}</span>
              <i className={`material-icons drSellerDropdownArrow ${open ? "open" : ""}`}>
                expand_more
              </i>
            </button>

            {open && (
              <div className="drSellerDropdownMenu">
                {profiles.map(p => {
                  const checked = p.id === activeProfileId;
                  return (
                    <label
                      key={p.id}
                      className={`drSellerDropdownItem ${checked ? "checked" : ""}`}
                    >
                      <input
                        type="radio"
                        name="drSellerProfile"
                        checked={checked}
                        onChange={() => handlePick(p.id)}
                      />
                      <span className="drSellerRadio" />
                      <span className="drSellerItemLabel">{labelFor(p)}</span>
                    </label>
                  );
                })}
                <button
                  type="button"
                  className="drSellerDropdownAddNew"
                  onClick={handleAddNew}
                >
                  <i className="material-icons">add</i>
                  <span>Add new seller</span>
                </button>
                <a
                  className="drSellerDropdownManage"
                  href="/settings/seller-profiles"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                >
                  <i className="material-icons">open_in_new</i>
                  <span>Manage all profiles</span>
                </a>
              </div>
            )}
          </div>
        ) : (
          <button className="drSellerSetupBtn" onClick={onAddNewProfile}>
            Set Up Profile
          </button>
        )}

        {activeProfile && (
          <button className="drSellerEditBtn" onClick={onEditProfile}>Edit</button>
        )}
        {loadedResearchProfileId &&
         activeProfileId &&
         loadedResearchProfileId !== activeProfileId && (
          <span className="drSellerSwitchHint">— different from the one used for current research</span>
        )}
      </div>

      <div className="drModeInfo">
        <span className="drSellerLabel">Speed:</span>
        <div className="drSellerDropdown" ref={modeRef}>
          <button
            type="button"
            className={`drSellerDropdownTrigger ${modeOpen ? "active" : ""}`}
            onClick={() => setModeOpen((o) => !o)}
          >
            <span className="drSellerDropdownText">
              {currentMode.icon} {currentMode.label}
            </span>
            <i className={`material-icons drSellerDropdownArrow ${modeOpen ? "open" : ""}`}>
              expand_more
            </i>
          </button>

          {modeOpen && (
            <div className="drSellerDropdownMenu">
              {MODES.map((m) => {
                const checked = m.value === mode;
                return (
                  <label
                    key={m.value}
                    className={`drSellerDropdownItem ${checked ? "checked" : ""}`}
                  >
                    <input
                      type="radio"
                      name="drResearchMode"
                      checked={checked}
                      onChange={() => {
                        onModeChange(m.value);
                        setModeOpen(false);
                      }}
                    />
                    <span className="drSellerRadio" />
                    <span className="drSellerItemLabel">
                      <span className="drModeItemTitle">
                        {m.icon} {m.label}
                      </span>
                      <span className="drModeItemMeta">
                        {m.time} · {m.credits} {m.credits === 1 ? "credit" : "credits"}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default SellerProfileBar;
